import { useMemo } from "react";
import { Link } from "react-router-dom";
import * as Tooltip from "@radix-ui/react-tooltip";
import { useTranslation } from "react-i18next";
import type { RenewalEvent } from "../lib/types";
import { layoutRenewals } from "../lib/renewalLayout";

interface RenewalCalendarProps {
  renewals: RenewalEvent[];
}

const GRIDLINE_PERCENTS: readonly number[] = [16.66, 33.33, 50, 66.66, 83.33];

/**
 * Home page "Renewal calendar — next 90 days" card (v2).
 *
 * Proto mapping: `.panel` shell → SectionCard-look, `.cal-wrap` body,
 * `.cal-legend` / `.cal-leg` items, `.timeline` track with `.tl-dot`.
 * All data/logic unchanged from PPP — only markup/classes updated.
 */
export function RenewalCalendar({ renewals }: RenewalCalendarProps): JSX.Element {
  const { t } = useTranslation("renewals");
  const { dots, totalShown, pastDueCount, overflowByColumn, byBucket, topUpcoming } = useMemo(
    () => layoutRenewals(renewals),
    [renewals],
  );

  return (
    <Tooltip.Provider delayDuration={300} skipDelayDuration={100}>
      {/* Panel shell — proto .panel */}
      <section
        className="bg-surface border border-border rounded-[11px] shadow-card overflow-hidden mb-4"
        aria-labelledby="cal-section-h"
      >
        {/* Card header — proto .card-h */}
        <div className="flex items-center justify-between px-4 py-[13px] border-b border-border-2">
          <h3
            id="cal-section-h"
            className="text-[12.5px] font-bold tracking-[.03em] uppercase text-muted"
          >
            {t("calendar_title")}
          </h3>
          <Link
            to="/renewals"
            className="text-[11px] font-semibold text-dim bg-surface-2 rounded-full px-[9px] py-0.5 hover:text-accent transition-colors"
          >
            {t("calendar_link")}
          </Link>
        </div>

        {/* Cal wrap — proto .cal-wrap */}
        <div className="p-[18px]">
          {/* Legend — proto .cal-legend */}
          <div className="flex gap-[15px] flex-wrap mb-[14px]">
            <CalLegItem color="#ef4444" count={byBucket.expired} label={t("legend_expired")} />
            <CalLegItem color="#f59e0b" count={byBucket.le30}    label={t("legend_le30")}    />
            <CalLegItem color="#fbbf24" count={byBucket.le60}    label={t("legend_le60")}    />
            <CalLegItem color="#10b981" count={byBucket.le90}    label={t("legend_le90")}    />
          </div>

          {/* Date labels */}
          <div className="hidden sm:flex justify-between text-[10px] text-dim mb-1.5 px-1">
            <span>+0d</span>
            <span>+15d</span>
            <span>+30d</span>
            <span>+45d</span>
            <span>+60d</span>
            <span>+75d</span>
            <span>+90d+</span>
          </div>
          <div className="flex sm:hidden justify-between text-[10px] text-dim mb-1.5 px-1">
            <span>+0d</span>
            <span>+45d</span>
            <span>+90d+</span>
          </div>

          {/* Timeline track — proto .timeline */}
          <div
            className="relative overflow-hidden rounded-[10px] border border-border-2"
            style={{
              height: 108,
              background: "linear-gradient(90deg, var(--red-soft), transparent 40%)",
            }}
          >
            {/* Today vertical line */}
            <div
              aria-hidden
              className="absolute top-0 bottom-5 left-[2%] w-[2px] bg-accent opacity-50"
            />

            {/* Gridlines */}
            {GRIDLINE_PERCENTS.map((p) => (
              <div
                key={p}
                aria-hidden
                className="absolute top-0 bottom-5 w-px bg-border-2"
                style={{ left: `${p}%` }}
              />
            ))}

            {/* Label row at bottom */}
            {[0, 16.66, 33.33, 50, 66.66, 83.33].map((p, i) => (
              <span
                key={p}
                aria-hidden
                className="absolute bottom-1 text-[10px] text-dim"
                style={{ left: `${p + 1}%` }}
              >
                +{[0, 15, 30, 45, 60, 75][i]}d
              </span>
            ))}

            {/* Empty state */}
            {dots.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center text-[12px] text-dim">
                {t("no_upcoming")}
              </div>
            )}

            {/* Dots */}
            {dots.map((d) => (
              <Tooltip.Root key={d.id}>
                <Tooltip.Trigger asChild>
                  <Link
                    to={`/entities/${d.entityId}`}
                    aria-label={`${d.entityName} (${d.country}) — ${d.type} renewal · ${d.rawValue}`}
                    className="absolute w-11 h-11 -m-[18px] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-full"
                    style={{
                      left: `${d.leftPercent}%`,
                      top: `${d.topPercent}%`,
                      transform: "translate(-50%, -50%)",
                    }}
                  >
                    <span
                      aria-hidden
                      className="tl-dot w-3 h-3 rounded-full border-2 border-surface shadow-sm transition-transform hover:scale-125"
                      style={{ background: d.color }}
                    />
                  </Link>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    side="top"
                    sideOffset={6}
                    className="z-50 max-w-xs rounded-md border border-border bg-surface px-2.5 py-1.5 text-[11px] text-text shadow-pop"
                  >
                    <div className="font-semibold">
                      {d.countryFlag} {d.entityName}
                    </div>
                    <div className="text-dim">
                      {d.country} · {d.type} · {d.rawValue}
                    </div>
                    <div className="text-muted">
                      {d.daysUntil < 0
                        ? t("overdue_label", { days: Math.abs(d.daysUntil) })
                        : t("in_days_label", { days: d.daysUntil })}
                    </div>
                    <Tooltip.Arrow className="fill-border" />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            ))}

            {/* Overflow badges */}
            {Object.entries(overflowByColumn).map(([col, n]) => (
              <span
                key={col}
                className="absolute bottom-1 text-[10px] text-dim tabular-nums"
                style={{ left: `${Number(col) * 16.66 + 4}%` }}
              >
                +{n}
              </span>
            ))}
          </div>

          {/* "Next up" callout */}
          {topUpcoming.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-dim shrink-0">{t("next_up_label")}</span>
              {topUpcoming.map((d) => (
                <Link
                  key={d.id}
                  to={`/entities/${d.entityId}`}
                  aria-label={`${d.entityName} — renewal in ${d.daysUntil} days`}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[12px] rounded-full border border-border hover:border-accent hover:text-accent min-h-[28px] transition-colors"
                >
                  <span aria-hidden>{d.countryFlag}</span>
                  <span className="truncate max-w-[28ch]">{d.entityName}</span>
                  <span className="text-dim tabular-nums">{d.daysUntil}d</span>
                </Link>
              ))}
            </div>
          )}

          {/* Footer */}
          <p className="text-[11px] text-dim mt-3">
            {t("calendar_footer", { count: totalShown, pastDue: pastDueCount })}
          </p>
        </div>
      </section>
    </Tooltip.Provider>
  );
}

function CalLegItem({
  color,
  count,
  label,
}: {
  color: string;
  count: number;
  label: string;
}): JSX.Element {
  return (
    <span className="flex items-center gap-[6px] text-[11.5px] text-muted">
      <span
        aria-hidden
        className="w-[9px] h-[9px] rounded-full shrink-0"
        style={{ background: color }}
      />
      <b className="font-semibold text-text tabular-nums">{count}</b>
      <span>{label}</span>
    </span>
  );
}
